import type { GeoProjection } from 'd3-geo';
import { arc, pie, type PieArcDatum } from 'd3-shape';
import map from 'lodash/map';
import sumBy from 'lodash/sumBy';
import React from 'react';

import { DEFAULT_FLOW_COLORS } from './constants';
import type { TooltipState } from './layers';
import type { PieMarkerItem, PieMarkerSlice } from './useMapData';

type TooltipUpdater = TooltipState | null | ((prev: TooltipState | null) => TooltipState | null);

interface PieMarkerLayerProps {
  projection: GeoProjection;
  data: PieMarkerItem[];
  onTooltip: (tooltip: TooltipUpdater) => void;
}

const MIN_RADIUS = 10;
const MAX_RADIUS = 40;

/**
 * Per-point pie/donut glyph layer — a new sibling to MarkerLayer (never
 * edits it), added for the Tanker Data Hub map's proportional category
 * breakdown per region. Reuses DEFAULT_FLOW_COLORS the same way FlowLayer
 * resolves per-group colors (layers.tsx's groupColors pattern), and the
 * same lifted onTooltip/TooltipState contract every other layer uses.
 */
export const PieMarkerLayer = React.memo(function PieMarkerLayer ({
  projection,
  data,
  onTooltip,
}: PieMarkerLayerProps) {
  const totals = map(data, (marker) => sumBy(marker.slices, 'value'));
  const maxTotal = Math.max(...totals, 1);
  const pieGenerator = pie<PieMarkerSlice>().value((d) => d.value).sort(null);
  const arcGenerator = arc<PieArcDatum<PieMarkerSlice>>().innerRadius(0).outerRadius(1);

  return (
    <g className="pie-marker-layer">
      {map(data, (marker, i) => {
        const pos = projection([marker.lon, marker.lat]);
        if (!pos) return null;

        const radius = MIN_RADIUS + Math.sqrt(totals[i] / maxTotal) * (MAX_RADIUS - MIN_RADIUS);
        const arcs = pieGenerator(marker.slices);

        return (
          <g
            key={`pie-marker-${marker.name ?? i}`}
            transform={`translate(${pos[0]}, ${pos[1]})`}
            style={{ cursor: 'pointer' }}
            onMouseEnter={(e) => onTooltip({
              x: e.clientX,
              y: e.clientY,
              content: (
                <div>
                  {marker.name && <div className="font-medium">{marker.name}</div>}
                  {map(marker.slices, (slice) => (
                    <div key={slice.label}>{slice.label}: {Math.round(slice.value).toLocaleString()}</div>
                  ))}
                </div>
              ),
            })}
            onMouseMove={(e) => onTooltip((prev) => (prev ? { ...prev, x: e.clientX, y: e.clientY } : null))}
            onMouseLeave={() => onTooltip(null)}
          >
            {map(arcs, (sliceArc, si) => (
              <path
                key={sliceArc.data.label}
                d={arcGenerator.outerRadius(radius)(sliceArc) ?? ''}
                fill={sliceArc.data.color ?? DEFAULT_FLOW_COLORS[si % DEFAULT_FLOW_COLORS.length]}
                stroke="#fff"
                strokeWidth={1}
              />
            ))}
          </g>
        );
      })}
    </g>
  );
});
